import { z } from 'zod';
import { ConversationStatusValueSchema } from '@wit-support/contracts';
import { FIELD_OPERATORS, FIELD_SOURCES, findFieldDef } from './field-catalog.js';
/**
 * Versioned, Zod-validated pipeline definition: a node graph walked from
 * `entryNodeId`. Supports branch conditions (`user_identified`,
 * `has_active_subscription`, generic `field_match`) and three actions. The
 * schema is the contract the runner, the db store
 * (`loadEnabledPipelineByKey` parses `pipeline.definition` with it) and the
 * admin editor all share — adding a node type is a localized change here +
 * the matching registry entry.
 *
 * Design notes:
 *  - Condition nodes carry `onTrue` / `onFalse` (nullable → end of branch).
 *  - Action nodes carry `next` (nullable → end). Params are per-type.
 *  - Node `id`s are graph-local strings (the map key + the `id` field must
 *    agree; the runner keys on the map, `id` is informational/round-trip).
 */
export const ConditionTypeSchema = z.enum([
    'user_identified',
    'has_active_subscription',
    'field_match',
]);
/** Predicate condition types — the two original boolean predicates (no params). */
export const PredicateConditionTypeSchema = z.enum(['user_identified', 'has_active_subscription']);
/**
 * Source profile a `field_match` condition reads from. Built from the single
 * `FIELD_SOURCES` tuple in `field-catalog.ts` — see M-067.
 */
export const FieldSourceSchema = z.enum(FIELD_SOURCES);
/**
 * Comparison operator a `field_match` condition applies. Built from the
 * single `FIELD_OPERATORS` tuple in `field-catalog.ts` — see M-067.
 */
export const FieldOperatorSchema = z.enum(FIELD_OPERATORS);
/**
 * Schedule delay presets for email action nodes. Declared locally here to keep
 * `packages/pipeline` dependency-light (no dep on `@wit-support/admin-contracts`).
 * The worker's SchedulePort adapter (which already depends on admin-contracts)
 * converts these string literals to milliseconds via `resolvePresetMs`.
 */
export const SchedulePresetSchema = z.enum(['+30m', '+1d', '+2d', '+3d', '+4d']);
export const ActionTypeSchema = z.enum([
    'send_template_email',
    'send_custom_email',
    'cancel_subscription',
    'delete_account',
    'set_conversation_status',
    'collect_input',
    'resolve_identity',
]);
/**
 * The conversation lifecycle status a `set_conversation_status` node can set.
 * Re-exported from `@wit-support/contracts` — canonical source of truth.
 */
export const ConversationStatusSchema = ConversationStatusValueSchema;
/** Operators that ignore their `value` (presence / strict-bool checks). */
const VALUELESS_OPERATORS = new Set(['exists', 'not_exists', 'is_true', 'is_false']);
/**
 * Predicate condition node: branches on one of the two original boolean
 * predicates (`user_identified` / `has_active_subscription`). No params — these
 * read derived flags / the prefetched subscription snapshot off the context.
 */
export const PredicateConditionNodeSchema = z.object({
    id: z.string().min(1, 'Укажите id узла'),
    kind: z.literal('condition'),
    type: PredicateConditionTypeSchema,
    onTrue: z.string().min(1).nullable(),
    onFalse: z.string().min(1).nullable(),
});
/**
 * Generic field_match condition node: reads a catalog field off the chosen
 * `source` profile and applies `operator` (optionally against `value`). The
 * superRefine below enforces operator/value consistency and that `fieldPath` is
 * a known path in the catalog for `source`.
 */
export const FieldMatchConditionNodeSchema = z
    .object({
    id: z.string().min(1, 'Укажите id узла'),
    kind: z.literal('condition'),
    type: z.literal('field_match'),
    source: FieldSourceSchema,
    fieldPath: z.string().min(1, 'Выберите поле'),
    operator: FieldOperatorSchema,
    value: z
        .union([z.string(), z.number(), z.boolean(), z.array(z.union([z.string(), z.number()]))])
        .optional(),
    onTrue: z.string().min(1).nullable(),
    onFalse: z.string().min(1).nullable(),
})
    .superRefine((node, ctx) => {
    // 1. fieldPath must be a known catalog path for the chosen source.
    const def = findFieldDef(node.source, node.fieldPath);
    if (!def) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['fieldPath'],
            message: `Неизвестное поле «${node.fieldPath}» для источника «${node.source}»`,
        });
        return;
    }
    // 2. The operator must be valid for the field's type.
    if (!def.operators.includes(node.operator)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['operator'],
            message: `Оператор «${node.operator}» не применим к полю «${node.fieldPath}»`,
        });
    }
    // 3. Operator/value consistency.
    if (VALUELESS_OPERATORS.has(node.operator)) {
        // value is ignored for presence / strict-bool checks — no further checks.
        return;
    }
    if (node.operator === 'in') {
        if (!Array.isArray(node.value) || node.value.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['value'],
                message: 'Оператор «in» требует непустой список значений',
            });
        }
        return;
    }
    // Remaining operators (equals/not_equals/gt/gte/lt/lte/contains) need a scalar.
    if (node.value === undefined || Array.isArray(node.value)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['value'],
            message: `Оператор «${node.operator}» требует одно значение`,
        });
    }
});
/** Any condition node — predicate or generic field_match. */
export const ConditionNodeSchema = z.union([
    PredicateConditionNodeSchema,
    FieldMatchConditionNodeSchema,
]);
/** Action: send a pre-made reply template (loaded by id at send time). */
export const SendTemplateEmailNodeSchema = z.object({
    id: z.string().min(1, 'Укажите id узла'),
    kind: z.literal('action'),
    type: z.literal('send_template_email'),
    params: z.object({
        templateId: z.string().min(1, 'Выберите шаблон'),
        /** When set, schedule the send for this preset delay instead of sending immediately. */
        delay: SchedulePresetSchema.optional(),
    }),
    next: z.string().min(1).nullable(),
});
/** Action: send a custom subject + HTML body. */
export const SendCustomEmailNodeSchema = z.object({
    id: z.string().min(1, 'Укажите id узла'),
    kind: z.literal('action'),
    type: z.literal('send_custom_email'),
    params: z.object({
        subject: z.string().min(1, 'Укажите тему письма'),
        html: z.string().min(1, 'Укажите текст письма'),
        /** When set, schedule the send for this preset delay instead of sending immediately. */
        delay: SchedulePresetSchema.optional(),
    }),
    next: z.string().min(1).nullable(),
});
/** Outcomes a `cancel_subscription` node can resolve to (mirrors `SubscriptionCancelResult.status`). */
export const CancelOutcomeSchema = z.enum([
    'cancelled',
    'already_cancelled',
    'no_subscription',
    'failed',
    'skipped_no_adapter',
]);
/**
 * Action: cancel the subscription via the payment port (`cancel-all` by
 * wit_user_id). Optional `outcomes` map routes each cancel outcome to its own
 * next node (`null` value → end of branch on that outcome). When `outcomes` is
 * ABSENT, back-compat behavior applies (see the runner): `failed` /
 * `skipped_no_adapter` STOP the run, the success-ish outcomes fall through to
 * `next`. With an explicit `outcomes` map the operator opts into routing every
 * outcome.
 */
export const CancelSubscriptionNodeSchema = z.object({
    id: z.string().min(1, 'Укажите id узла'),
    kind: z.literal('action'),
    type: z.literal('cancel_subscription'),
    params: z.object({}),
    next: z.string().min(1).nullable(),
    // NOTE: `z.partialRecord` (not `z.record`): in Zod 4 a record with an enum key
    // is EXHAUSTIVE (every outcome required). A cancel node routes only the
    // outcomes it cares about, so the map must be partial.
    outcomes: z.partialRecord(CancelOutcomeSchema, z.string().min(1).nullable()).optional(),
});
/** Outcomes a `delete_account` node can resolve to (mirrors `AccountDeletionResult.status` + the no-user short-circuit). */
export const DeleteAccountOutcomeSchema = z.enum([
    'queued',
    'already_deleted',
    'no_user',
    'failed',
]);
/**
 * Action: request GDPR deletion of the contact's app account via the
 * account-deletion port (fire-and-forget — the upstream `POST .../delete`
 * enqueues an async deletion job; we record `requestId` + `status` and route by
 * outcome but do NOT poll to completion). Optional `outcomes` map routes each
 * outcome to its own next node (`null` value → end of branch on that outcome),
 * EXACTLY mirroring `cancel_subscription`. When `outcomes` is ABSENT, back-compat
 * behavior applies (see the runner): `failed` STOPS the run, the success-ish
 * outcomes (`queued` / `already_deleted` / `no_user`) fall through to `next`.
 *
 * IRREVERSIBLE: deleting an account cannot be undone. The editor surfaces an
 * explicit warning; this action is allowed in any pipeline (auto + manual).
 */
export const DeleteAccountNodeSchema = z.object({
    id: z.string().min(1, 'Укажите id узла'),
    kind: z.literal('action'),
    type: z.literal('delete_account'),
    params: z.object({}),
    next: z.string().min(1).nullable(),
    // NOTE: `z.partialRecord` (not `z.record`) — same exhaustiveness rationale as
    // `cancel_subscription`: a delete node routes only the outcomes it cares about.
    outcomes: z.partialRecord(DeleteAccountOutcomeSchema, z.string().min(1).nullable()).optional(),
});
/**
 * Action: set the conversation's lifecycle status (e.g. resolve it as the final
 * step of an auto-response pipeline). Drives the `conversationStatusPort`.
 */
export const SetConversationStatusNodeSchema = z.object({
    id: z.string().min(1, 'Укажите id узла'),
    kind: z.literal('action'),
    type: z.literal('set_conversation_status'),
    params: z.object({ status: ConversationStatusSchema }),
    next: z.string().min(1).nullable(),
});
export const CollectInputOutcomeSchema = z.enum(['collected', 'escalated']);
export const ResolveIdentityOutcomeSchema = z.enum(['resolved', 'unresolved']);
export const ExtractFieldKindSchema = z.enum(['email', 'card_last4', 'text']);
export const ExtractFieldSchema = z.object({
    key: z.string().min(1, 'Укажите ключ поля'),
    kind: ExtractFieldKindSchema,
    required: z.boolean(),
    description: z.string().min(1, 'Опишите поле для извлечения'),
});
export const CollectInputNodeSchema = z
    .object({
    id: z.string().min(1, 'Укажите id узла'),
    kind: z.literal('action'),
    type: z.literal('collect_input'),
    params: z.object({
        questionTemplateId: z.string().min(1, 'Выберите шаблон вопроса'),
        extract: z.array(ExtractFieldSchema).min(1, 'Добавьте хотя бы одно поле'),
        requireAny: z.array(z.string().min(1)).optional(),
        timeoutHours: z.number().int().positive().default(48),
    }),
    outcomes: z.object({
        collected: z.string().min(1).nullable(),
        escalated: z.string().min(1).nullable(),
    }),
})
    .superRefine((node, ctx) => {
    // `requireAny` defaults downstream to the set of required-field keys. A node where
    // every extract field has `required:false` AND no explicit `requireAny` is provided
    // yields an empty satisfaction set → `[].some(...)===false` → always escalates, even
    // on a perfect extraction. Require that at least one field is required OR an explicit
    // non-empty `requireAny` is supplied so the `collected` outcome is reachable.
    const { extract, requireAny } = node.params;
    const hasRequiredField = extract.some((f) => f.required);
    const hasExplicitRequireAny = Array.isArray(requireAny) && requireAny.length > 0;
    if (!hasRequiredField && !hasExplicitRequireAny) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['params', 'extract'],
            message: 'Отметьте хотя бы одно обязательное поле',
        });
    }
});
export const ResolveIdentityNodeSchema = z.object({
    id: z.string().min(1, 'Укажите id узла'),
    kind: z.literal('action'),
    type: z.literal('resolve_identity'),
    params: z.object({
        by: z.literal('email'), // v1: только email (card_last4 — после upstream-эндпоинта)
        sourceKey: z.string().min(1, 'Укажите ключ из context'),
    }),
    outcomes: z.object({
        resolved: z.string().min(1).nullable(),
        unresolved: z.string().min(1).nullable(),
    }),
});
export const ActionNodeSchema = z.discriminatedUnion('type', [
    SendTemplateEmailNodeSchema,
    SendCustomEmailNodeSchema,
    CancelSubscriptionNodeSchema,
    DeleteAccountNodeSchema,
    SetConversationStatusNodeSchema,
    CollectInputNodeSchema,
    ResolveIdentityNodeSchema,
]);
/**
 * Any node. NOT a `discriminatedUnion('kind')` any more: a discriminatedUnion
 * member cannot itself be a union, and the condition branch is now a union of
 * the predicate node + the field_match node (the latter carries a superRefine,
 * which also can't sit inside a discriminatedUnion). A plain `z.union` over all
 * member schemas keeps every member's own discriminator (`kind` + `type`) and
 * superRefine intact, so legacy persisted `user_identified` /
 * `has_active_subscription` nodes still parse unchanged, and parse errors stay
 * localized to the matching member.
 */
export const NodeSchema = z.union([
    PredicateConditionNodeSchema,
    FieldMatchConditionNodeSchema,
    SendTemplateEmailNodeSchema,
    SendCustomEmailNodeSchema,
    CancelSubscriptionNodeSchema,
    DeleteAccountNodeSchema,
    SetConversationStatusNodeSchema,
    CollectInputNodeSchema,
    ResolveIdentityNodeSchema,
]);
/**
 * Outgoing edges of a node (condition → onTrue/onFalse; action → next; a cancel
 * node additionally exposes its `outcomes` targets so the dangling-ref + cycle
 * checks below cover every edge a run can actually follow).
 */
const outgoingEdges = (node) => {
    if (node.kind === 'condition')
        return [node.onTrue, node.onFalse];
    if (node.type === 'collect_input')
        return [node.outcomes.collected, node.outcomes.escalated];
    if (node.type === 'resolve_identity')
        return [node.outcomes.resolved, node.outcomes.unresolved];
    if ((node.type === 'cancel_subscription' || node.type === 'delete_account') && node.outcomes) {
        return [node.next, ...Object.values(node.outcomes)];
    }
    return [node.next];
};
export const PipelineDefinitionSchema = z
    .object({
    version: z.literal(1),
    entryNodeId: z.string().min(1, 'Выберите стартовый узел'),
    nodes: z.record(z.string(), NodeSchema),
})
    /**
     * Graph-integrity refinement — the single place every call site (pg
     * create/update via `parsePipelineDefinition`, the admin editor's
     * `safeParse`) enforces referential soundness. Structurally-valid but
     * referentially-broken definitions (dangling pointers, empty graph,
     * key≠id, cycles) are rejected here so a broken ENABLED pipeline can never
     * be persisted and fire a customer-facing email before aborting on a
     * dangling pointer.
     */
    .superRefine((def, ctx) => {
    const entries = Object.entries(def.nodes);
    // 1. Non-empty graph.
    if (entries.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['nodes'],
            message: 'Граф пуст: добавьте хотя бы один узел',
        });
        return;
    }
    // 2. Map key must equal the node's own id.
    for (const [key, node] of entries) {
        if (key !== node.id) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['nodes', key, 'id'],
                message: `Ключ узла «${key}» не совпадает с его id «${node.id}»`,
            });
        }
    }
    const ids = new Set(entries.map(([key]) => key));
    // 3. entryNodeId must reference an existing node.
    if (!ids.has(def.entryNodeId)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['entryNodeId'],
            message: `Стартовый узел «${def.entryNodeId}» не найден среди узлов`,
        });
    }
    // 4. Every non-null edge must reference an existing node.
    for (const [key, node] of entries) {
        for (const target of outgoingEdges(node)) {
            if (target !== null && !ids.has(target)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['nodes', key],
                    message: `Узел «${key}» ссылается на несуществующий узел «${target}»`,
                });
            }
        }
    }
    // 5. No cycle reachable from entryNodeId. Only walk if the entry and all
    //    edges resolve (issues above already flag the broken refs); a DFS over
    //    resolvable edges detects back-edges.
    if (ids.has(def.entryNodeId)) {
        const onStack = new Set();
        const done = new Set();
        let cyclic = false;
        const walk = (id) => {
            if (cyclic)
                return;
            if (onStack.has(id)) {
                cyclic = true;
                return;
            }
            if (done.has(id))
                return;
            onStack.add(id);
            const node = def.nodes[id];
            if (node) {
                for (const target of outgoingEdges(node)) {
                    if (target !== null && ids.has(target))
                        walk(target);
                }
            }
            onStack.delete(id);
            done.add(id);
        };
        walk(def.entryNodeId);
        if (cyclic) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['nodes'],
                message: 'Граф содержит цикл, достижимый из стартового узла',
            });
        }
    }
});
/**
 * Parse + validate an unknown blob (e.g. `pipeline.definition` jsonb) into a
 * typed `PipelineDefinition`. Throws ZodError on a malformed definition — the
 * caller (db store) surfaces it so a bad row never reaches the runner.
 */
export const parsePipelineDefinition = (raw) => PipelineDefinitionSchema.parse(raw);
/**
 * THE ONE PLACE a node type declares «I put a letter in front of the customer,
 * and this is which letter».
 *
 * Everything about pipeline mail hangs off this map: the resend guard key the
 * executor asks with, the identity the executed step REPORTS on its `result`
 * jsonb, and the static list the batch preview shows the operator. Before it
 * existed, each of those derived «which letter» on its own, and `collect_input`
 * — a node whose whole purpose is to mail a question — was in exactly ONE of the
 * three. A letter invisible to the guard is a letter that gets sent twice.
 *
 * Written as a TOTAL map over the action types rather than an `if` chain so the
 * classification is enforced by the COMPILER: add a node type and this object
 * stops type-checking until you have said, for that type, whether it mails
 * anything. That is the whole safety property — a new sending node cannot slip
 * past the guard by omission, because omission does not compile.
 *
 * `null` = this node sends nothing.
 */
const emailIdentityByActionType = {
    send_template_email: (node) => ({ kind: 'template', templateId: node.params.templateId }),
    send_custom_email: (node) => ({ kind: 'custom', subject: node.params.subject }),
    // A question is a letter like any other — it just also parks the run.
    collect_input: (node) => ({ kind: 'template', templateId: node.params.questionTemplateId }),
    cancel_subscription: () => null,
    delete_account: () => null,
    set_conversation_status: () => null,
    resolve_identity: () => null,
};
/** The letter this node sends, or `null` when it sends none. See {@link emailIdentityByActionType}. */
export const emailIdentityOfNode = (node) => {
    if (node.kind !== 'action')
        return null;
    const read = emailIdentityByActionType[node.type];
    return read(node);
};
/**
 * The `pipeline_run_step.result` key an email identity is REPORTED under.
 *
 * One function for both ends of the same wire: the executor writes this key onto
 * the step, and `hasRecentPipelineEmailSend` (`packages/db/src/pipeline-store.ts`)
 * reads `result->>'<key>'` back out to decide «has this letter already gone to
 * this contact?». Two independent spellings of one jsonb key is a silent guard
 * failure — the query simply matches nothing — so neither side is allowed its own.
 */
export const emailIdentityStepField = (kind) => kind === 'template' ? 'templateId' : 'subject';
/** The value stored under {@link emailIdentityStepField}. */
export const emailIdentityStepValue = (identity) => identity.kind === 'template' ? identity.templateId : identity.subject;
/** The `{ <field>: <value> }` pair an executing node spreads onto its step result. */
export const emailIdentityStepFields = (identity) => ({
    [emailIdentityStepField(identity.kind)]: emailIdentityStepValue(identity),
});
/**
 * Every letter this graph COULD send, deduped and in stable node-id order.
 *
 * Answers the one question the batch preview must ask before it fans a pipeline
 * out over hundreds of conversations: "which letters is this about to put in
 * front of a customer?" It walks ALL nodes rather than the entry path, because a
 * branch that only fires on `cancel_failed` still ends in a real email, and the
 * safeguard has to be conservative — over-reporting a letter costs a skip the
 * operator can override, while missing one costs a duplicate send.
 *
 * `delay`ed sends are included: a scheduled letter is as binding as a sent one.
 */
export const listPipelineEmailIdentities = (definition) => {
    const seen = new Set();
    const out = [];
    for (const nodeId of Object.keys(definition.nodes).sort()) {
        const node = definition.nodes[nodeId];
        if (!node)
            continue;
        const identity = emailIdentityOfNode(node);
        if (!identity)
            continue;
        const dedupeKey = `${identity.kind[0]}:${emailIdentityStepValue(identity)}`;
        if (seen.has(dedupeKey))
            continue;
        seen.add(dedupeKey);
        out.push(identity);
    }
    return out;
};
//# sourceMappingURL=definition.js.map